import {
  Box,
  Button,
  HStack,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  useDisclosure,
} from "@chakra-ui/react";
import { t } from "@lingui/macro";
import {
  useCloseWipBulkMutation,
  useLockWipBulkMutation,
  useOpenWipBulkMutation,
  useProjectProgressStatisticsExportLazyQuery,
  useProjectsCommissionExportLazyQuery,
  useUnlockWipBulkMutation,
} from "@src/__generated__/graphql";
import {
  DataTable,
  ModalConfirm,
  ModalInputDatePicker,
  MonthPicker,
  NavBar as NavBarUI,
} from "@src/components/ui-kit";
import { Icon } from "@src/components/ui-kit/Icon";
import { onError } from "@src/utils/apollo";
import { useStore as useGlobalStore } from "@src/utils/hooks";
import { observer } from "mobx-react-lite";
import React from "react";
import { toApiDate } from "../../../../utils/dates";
import Actions from "./Actions";
import ExportWIPBtn from "./ExportWIPBtn";
import { useStore } from "./Store";

const NavBar = () => {
  const { authStore } = useGlobalStore();
  const store = useStore();
  const { wipPeriodsStore } = store;

  const commissionDatePickerModal = useDisclosure();
  const progressDatePickerModal = useDisclosure();
  const unlockWipBulkDialog = useDisclosure();
  const lockWipBulkDialog = useDisclosure();
  const openWipBulkDialog = useDisclosure();
  const closeWipBulkDialog = useDisclosure();

  const bulkMutationParams = () => ({
    periodId: wipPeriodsStore.currentPeriod?.id!,
    where: store.queryParams.filters?.where,
  });

  const [lockWipBulkMutation, { loading: loadingLock }] =
    useLockWipBulkMutation({
      variables: bulkMutationParams(),
      onCompleted() {
        store.refetchProjectsWip();
        store.refetchSummary();
      },
      ...onError(),
    });

  const [unlockWipBulkMutation, { loading: loadingUnlock }] =
    useUnlockWipBulkMutation({
      variables: bulkMutationParams(),
      onCompleted() {
        store.refetchProjectsWip();
        store.refetchSummary();
      },
      ...onError(),
    });

  const [openWipBulkMutation, { loading: loadingOpen }] =
    useOpenWipBulkMutation({
      variables: bulkMutationParams(),
      onCompleted() {
        store.refetchProjectsWip();
        store.refetchSummary();
      },
      ...onError(),
    });

  const [closeWipBulkMutation, { loading: loadingClose }] =
    useCloseWipBulkMutation({
      variables: bulkMutationParams(),
      onCompleted() {
        store.refetchProjectsWip();
        store.refetchSummary();
      },
      ...onError(),
    });

  const [fetchProgress] = useProjectProgressStatisticsExportLazyQuery({
    onCompleted(data) {
      const projectProgressStatisticsExport =
        data?.projectProgressStatisticsExport;

      if (projectProgressStatisticsExport) {
        window.location.href = projectProgressStatisticsExport;
      }
    },
    ...onError(),
  });

  const [fetchCommission] = useProjectsCommissionExportLazyQuery({
    onCompleted(data) {
      if (data?.projectsCommissionExport) {
        window.location.href = data.projectsCommissionExport;
      }
    },
    ...onError(),
  });

  return (
    <React.Fragment>
      <DataTable.NavBar>
        <HStack justifyContent="space-between">
          <NavBarUI>
            <NavBarUI.Item
              active={!store.projectManagerFilter.value.length}
              onClick={() => store.projectManagerFilter.clear()}
            >
              {t`All`}
            </NavBarUI.Item>

            <NavBarUI.Item
              active={store.projectManagerFilter.value.includes(
                authStore.user?.id!,
              )}
              onClick={() =>
                store.projectManagerFilter.setValue(authStore.user?.id!)
              }
            >
              {t`My`}
            </NavBarUI.Item>

            {wipPeriodsStore.currentPeriod && (
              <MonthPicker
                className="mx-4"
                value={wipPeriodsStore.currentPeriod}
                onChange={({ id }) => wipPeriodsStore.setPeriodId(id)}
                options={wipPeriodsStore.options}
                returnToCurrentMonth
              />
            )}

            {!!store.projectsWip.length &&
              store.summary &&
              wipPeriodsStore.currentPeriod && (
                <Actions
                  size="md"
                  onOpen={openWipBulkDialog.onOpen}
                  openLoading={loadingOpen}
                  onClose={closeWipBulkDialog.onOpen}
                  closeLoading={loadingClose}
                  onLock={lockWipBulkDialog.onOpen}
                  lockLoading={loadingLock}
                  onUnlock={unlockWipBulkDialog.onOpen}
                  unlockLoading={loadingUnlock}
                  isClosed={store.summary.is_period_closed}
                  isLocked={store.summary.is_period_locked}
                />
              )}
          </NavBarUI>
          {wipPeriodsStore.currentPeriod && (
            <Box>
              <Menu>
                <MenuButton
                  as={Button}
                  colorScheme="grey"
                  leftIcon={<Icon name="download-02" />}
                  rightIcon={<Icon name="chevron-down" />}
                  variant="outline"
                >{t`Export`}</MenuButton>

                <MenuList>
                  <MenuItem
                    onClick={commissionDatePickerModal.onOpen}
                  >{t`Commissions`}</MenuItem>
                  <ExportWIPBtn
                    periodId={store.wipPeriodsStore.currentPeriod!.id}
                  />
                  <MenuItem onClick={progressDatePickerModal.onOpen}>
                    {t`Progress statistics`}
                  </MenuItem>
                </MenuList>
              </Menu>
            </Box>
          )}
        </HStack>
      </DataTable.NavBar>

      <ModalInputDatePicker
        {...progressDatePickerModal}
        allowRange
        title={t`Choose start and end date`}
        confirmLabel={t`Generate`}
        onConfirm={(range) => {
          return fetchProgress({
            variables: {
              from: toApiDate(range.start),
              to: toApiDate(range.end),
              wip_period_ids: [wipPeriodsStore.currentPeriod!.id],
            },
          });
        }}
      />
      <ModalInputDatePicker
        {...commissionDatePickerModal}
        allowRange
        title={t`Choose start and end date`}
        confirmLabel={t`Generate`}
        onConfirm={(range) => {
          return fetchCommission({
            variables: {
              from: toApiDate(range.start),
              to: toApiDate(range.end),
            },
          });
        }}
      />
      <ModalConfirm {...openWipBulkDialog} onConfirm={openWipBulkMutation} />
      <ModalConfirm {...closeWipBulkDialog} onConfirm={closeWipBulkMutation} />
      <ModalConfirm {...lockWipBulkDialog} onConfirm={lockWipBulkMutation} />
      <ModalConfirm
        {...unlockWipBulkDialog}
        onConfirm={unlockWipBulkMutation}
      />
    </React.Fragment>
  );
};

export default observer(NavBar);
